import {
  IconClockLine,
  Pill,
  Text,
  Tooltip,
  TruncateText,
  View,
} from "@instructure/ui";

import React, { useContext, useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import "./../curriculum-map.css";

import { t } from "i18next";
import Skeleton from "react-loading-skeleton";
import { TermsContext } from "../../../utils/Context";
import { TruncateToolTip } from "../../../utils/TruncateTooltip";
import { IssuersContext } from "../../../views/CurriculumMap";
import {
  getCurrentVersion,
  getFutureVersions,
  getVersionByDate,
  isArchivedByDate,
  pendingArchive,
  sortOutcomes,
} from "../../LearningOutcomes/utils";
import MappingButton from "./MappingButton";

const Mapping = ({
  coursesWithOutcomes,
  filteredLOs,
  attainments,
  rowTrigger,
  setRowTrigger,
  learningOutcomeType,
  dash,
  editable,
  selectedAssessmentPlan,
  badgeMappings,
  setBadgeMappings,
  date,
  futureMap,
  fetchMappingData,
  showArchived,
}) => {
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const issuers = useContext(IssuersContext);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (fetchMappingData) {
      fetchMappingData(rows, columns);
    }
  }, [rows, columns]);

  useEffect(() => {
    if (coursesWithOutcomes) {
      coursesWithOutcomes.forEach((course) => {
        if (course.code === course.course_summary.code) {
          course.is_default = true;
        }
      });
    }
  }, [rows, columns]);

  const getRowName = (name, statement, course) => {
    return (
      <View
        as="div"
        height="100%"
        padding="small"
        margin={!course ? "0 0 0 small" : "0"}
      >
        {course ? (
          <Tooltip
            color="primary-inverse"
            renderTip={
              <div style={{ maxWidth: 250 }}>
                {name +
                  ", ex: " +
                  course.course_summary.representative_section_name}
              </div>
            }
            placement="end"
          >
            <Text size="small" tabIndex={0}>
              <TruncateText maxLines={5}>
                <span style={{ fontWeight: "bold" }}>{name}</span>{" "}
              </TruncateText>
            </Text>
          </Tooltip>
        ) : (
          <TruncateToolTip
            renderTip={
              <div style={{ maxWidth: 250 }}>
                <span style={{ fontWeight: "bold" }}>{name}</span>
                {": "}
                {statement}
              </div>
            }
            maxLines={4}
            placement="end"
          >
            <Text size="small">
              <Text size="small" weight="bold">
                {name}
              </Text>
              : {statement}
            </Text>
          </TruncateToolTip>
        )}
      </View>
    );
  };

  const getColumnName = (name, statement) => {
    return (
      <View as="div" padding="small" height="100%">
        <TruncateToolTip
          renderTip={
            <div style={{ maxWidth: 250 }}>
              <span style={{ fontWeight: "bold" }}>{name}</span>
              {": "}
              {statement}
            </div>
          }
          maxLines={6}
        >
          <Text size="small" weight="normal">
            <Text size="small" weight="bold">
              {name}
            </Text>
            : {statement}
          </Text>
        </TruncateToolTip>
      </View>
    );
  };

  useEffect(() => {
    if (rowTrigger) {
      setRowTrigger(false);
      let rowsArr = [];
      coursesWithOutcomes.forEach((course) => {
        let ac_sub;
        if (course.learning_outcomes) {
          ac_sub = course.course_summary.academic_subject.title;
        }
        rowsArr.push({
          name: getRowName(course.code, "", course),
          course: course,
          ac_sub,
          style: "course-level",
        });
        if (course.learning_outcomes) {
          course.learning_outcomes = sortOutcomes(course.learning_outcomes);
          course.learning_outcomes.forEach((outcome) => {
            if (!isArchivedByDate(outcome, date) || showArchived) {
              const version = getVersionByDate(outcome, date);
              const futureVersions = getFutureVersions(outcome).filter(
                (futureVersion) =>
                  new Date(futureVersion.effective_term.start_at) > date
              );
              rowsArr.push({
                name: (
                  <>
                    {getRowName(version.name, version.statement)}
                    {futureVersions.length > 0 && (
                      <div
                        style={{ position: "absolute", bottom: 5, right: 5 }}
                      >
                        <Tooltip
                          color="primary-inverse"
                          renderTip={
                            <div style={{ maxWidth: 250 }}>
                              {futureVersions.map((futureVersion, key) => (
                                <Text size="small" key={key}>
                                  <Text weight="bold" size="small">
                                    {findVersionInsightsTermName(futureVersion)}
                                    :{" "}
                                  </Text>
                                  {futureVersion.statement}
                                  <br />
                                </Text>
                              ))}
                            </div>
                          }
                        >
                          <Pill
                            color="info"
                            renderIcon={<IconClockLine />}
                            tabIndex={0}
                          >
                            {t("learningOutcomes.upcoming")}
                          </Pill>
                        </Tooltip>
                      </div>
                    )}
                    {isArchivedByDate(outcome, date) && (
                      <div
                        style={{ position: "absolute", bottom: 5, right: 5 }}
                      >
                        <Pill>
                          {t("learningOutcomes.archive.archived")}{" "}
                          {outcome.archive_term.insights_term_id}
                        </Pill>
                      </div>
                    )}
                    {!isArchivedByDate(outcome, date) &&
                      pendingArchive(outcome) && (
                        <div
                          style={{ position: "absolute", bottom: 5, right: 5 }}
                        >
                          <Pill>
                            {t("learningOutcomes.archive.archiving")}{" "}
                            {outcome.archive_term.insights_term_id}
                          </Pill>
                        </div>
                      )}
                  </>
                ),
                course: version,
                versions: [getCurrentVersion(outcome), ...futureVersions],
                futureVersions: futureVersions,
                ac_sub,
              });
            }
          });
        }
      });
      setRows(rowsArr);
    }
  }, [rowTrigger]);

  const findVersionInsightsTermName = (futureVersion) => {
    try {
      const canvasTerm = canvasTerms.find(
        (t) => t.id === futureVersion.effective_term.ddl_id
      );
      const insightsTerm = insightsTerms.find(
        (t) => t.id === canvasTerm.insights_term_id
      );
      return insightsTerm.name;
    } catch {
      return "undefined";
    }
  };

  function createMappingKey(row, column, mapping) {
    const rowId = row.course.id;
    const columnId = column.key;
    const isMapped = !!mapping;
    const attainmentLevel = mapping?.properties?.degreesOfLearning || "None";
    const hasBadge = !!badgeMappings.find(
      (badgeMapping) =>
        badgeMapping.sourceOutcomeId === row.course.id &&
        badgeMapping.targetOutcomeId === column.key
    );
    const hasLoSync = !!mapping?.properties?.lo_sync;

    return `${rowId}_${columnId}_${isMapped}_${attainmentLevel}_${hasBadge}_${hasLoSync}`;
  }

  useEffect(() => {
    let columnsArr = [];
    filteredLOs.forEach((lo) => {
      if (!isArchivedByDate(lo, date) || showArchived) {
        const version = getVersionByDate(lo, date);
        const futureVersions = getFutureVersions(lo).filter(
          (futureVersion) =>
            new Date(futureVersion.effective_term.start_at) > date
        );
        columnsArr.push({
          versions: [getCurrentVersion(lo), ...futureVersions],
          futureVersions: futureVersions,
          outcomeAssignments: version.assignmentTypes,
          unformatted: {
            name: version.name,
            statement: version.statement,
            outcome_type: version.outcome_type,
            archive_term: version.archive_term,
          },
          key: version.id,
          width: 225,
          renderCell: ({ row, column }) => {
            const mapping = row.course.mappings.find(
              (mapping) => mapping.to === version.id
            );

            return (
              <MappingButton
                key={createMappingKey(row, column, mapping)}
                mapping={mapping}
                row={row}
                column={column}
                dash={dash}
                badgeMappings={badgeMappings}
                date={date}
                selectedAssessmentPlan={selectedAssessmentPlan}
                learningOutcomeType={learningOutcomeType}
                attainments={attainments}
                setBadgeMappings={setBadgeMappings}
                futureMap={futureMap}
                rows={rows}
                coursesWithOutcomes={coursesWithOutcomes}
                editable={editable}
              />
            );
          },
          name: (
            <>
              {getColumnName(version.name, version.statement)}
              {futureVersions.length > 0 && (
                <div style={{ position: "absolute", bottom: 5, right: 5 }}>
                  <Tooltip
                    color="primary-inverse"
                    renderTip={
                      <div style={{ maxWidth: 250 }}>
                        {futureVersions.map((futureVersion, key) => (
                          <Text size="small" key={key}>
                            <Text weight="bold" size="small">
                              {findVersionInsightsTermName(futureVersion)}:{" "}
                            </Text>
                            {futureVersion.statement}
                            <br />
                          </Text>
                        ))}
                      </div>
                    }
                  >
                    <Pill color="info" renderIcon={<IconClockLine />}>
                      {t("learningOutcomes.upcoming")}
                    </Pill>
                  </Tooltip>
                </div>
              )}
              {isArchivedByDate(lo, date) && (
                <div style={{ position: "absolute", bottom: 5, right: 5 }}>
                  <Pill>
                    {t("learningOutcomes.archive.archived")}{" "}
                    {lo.archive_term.insights_term_id}
                  </Pill>
                </div>
              )}
              {!isArchivedByDate(lo, date) && pendingArchive(lo) && (
                <div style={{ position: "absolute", bottom: 5, right: 5 }}>
                  <Pill>
                    {t("learningOutcomes.archive.archiving")}{" "}
                    {lo.archive_term.insights_term_id}
                  </Pill>
                </div>
              )}
            </>
          ),
        });
      }
    });
    sortOutcomes(columnsArr, (column) => column.unformatted);
    columnsArr.unshift({ key: "name", name: "", width: 250, frozen: true });
    setColumns(columnsArr);
  }, [filteredLOs, rowTrigger, badgeMappings, issuers, canvasTerms, editable]);

  return (
    <div id="curriculumMap" className="curriculumMap">
      <View className="viewbox">
        {rows.length > 0 ? (
          <DataGrid
            className="rdg-light dataGridContainer"
            rowHeight={120}
            headerRowHeight={160}
            columns={columns}
            rows={rows}
            rowClass={(row) => row.style ?? undefined}
          />
        ) : (
          <Skeleton
            count={4}
            style={{ height: 120, borderRadius: 5, marginBottom: 5 }}
          />
        )}
      </View>
    </div>
  );
};

export default Mapping;
